import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Pin } from '@gps-hunt/shared';

interface MapContainerProps {
  subscriptionKey: string;
  center?: [number, number]; // [longitude, latitude]
  zoom?: number;
  pins?: Pin[];
  userLocation?: { latitude: number; longitude: number; accuracy: number };
  onMapClick?: (latitude: number, longitude: number) => void;
  onPinClick?: (pin: Pin) => void;
  className?: string;
}

/**
 * Azure Maps container component for the hider map view
 */
export const MapContainer: React.FC<MapContainerProps> = ({
  subscriptionKey,
  center, // No default - will use userLocation for initial center
  zoom = 15,
  pins = [],
  userLocation,
  onMapClick,
  onPinClick,
  className = '',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<atlas.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const dataSourceRef = useRef<atlas.source.DataSource | null>(null);
  const userMarkerRef = useRef<atlas.HtmlMarker | null>(null);
  const hasInitialCenterRef = useRef(false); // Track if we've done initial centering

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Only set initial center if we have userLocation, otherwise use a generic world view
    const initialCenter = userLocation 
      ? [userLocation.longitude, userLocation.latitude] as [number, number]
      : center || [0, 0] as [number, number];
    const initialZoom = userLocation ? zoom : (center ? zoom : 2);
    
    if (userLocation || center) {
      hasInitialCenterRef.current = true;
    }

    const map = new atlas.Map(mapRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      style: 'night', // Dark theme
      language: 'en-US',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: subscriptionKey,
      },
    });

    map.events.add('ready', () => {
      // Create data source for pins
      const dataSource = new atlas.source.DataSource();
      map.sources.add(dataSource);
      dataSourceRef.current = dataSource;

      // Add symbol layer for pins
      const symbolLayer = new atlas.layer.SymbolLayer(dataSource, undefined, {
        iconOptions: {
          image: 'pin-red',
          size: 1.2,
          anchor: 'bottom',
        },
        textOptions: {
          textField: ['get', 'sequenceOrder'],
          offset: [0, -2.5],
          color: '#FFFFFF',
          size: 14,
        },
      });
      map.layers.add(symbolLayer);

      // Handle map clicks
      map.events.add('click', (e: atlas.MapMouseEvent) => {
        if (e.position && onMapClick) {
          onMapClick(e.position[1], e.position[0]); // lat, lng
        }
      });

      // Handle pin clicks
      map.events.add('click', symbolLayer, (e: atlas.MapMouseEvent) => {
        if (e.shapes && e.shapes.length > 0 && onPinClick) {
          const shape = e.shapes[0] as atlas.Shape;
          const properties = shape.getProperties();
          const pinData = properties as Pin;
          onPinClick(pinData);
          e.preventDefault?.();
        }
      });

      setIsLoaded(true);
    });

    mapInstanceRef.current = map;

    return () => {
      map.dispose();
      mapInstanceRef.current = null;
    };
  }, [subscriptionKey]);

  // Center map only on first valid user location (not on every update)
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded || hasInitialCenterRef.current) return;
    
    // Center on user location the first time we get one
    if (userLocation) {
      mapInstanceRef.current.setCamera({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: zoom,
      });
      hasInitialCenterRef.current = true;
    }
  }, [userLocation, zoom, isLoaded]);

  // Update pins on the map
  useEffect(() => {
    if (!dataSourceRef.current || !isLoaded) return;

    dataSourceRef.current.clear();

    pins.forEach((pin) => {
      const point = new atlas.data.Feature(
        new atlas.data.Point([pin.longitude, pin.latitude]),
        {
          ...pin,
          sequenceOrder: (pin.sequenceOrder + 1).toString(), // 1-indexed display
        }
      );
      dataSourceRef.current?.add(point);
    });
  }, [pins, isLoaded]);

  // Update user location marker
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded || !userLocation) return;

    // Remove existing marker
    if (userMarkerRef.current) {
      mapInstanceRef.current.markers.remove(userMarkerRef.current);
    }

    // Create user location marker with accuracy circle
    const markerHtml = `
      <div style="position: relative;">
        <div style="
          width: ${Math.min(userLocation.accuracy * 2, 100)}px;
          height: ${Math.min(userLocation.accuracy * 2, 100)}px;
          border-radius: 50%;
          background: rgba(0, 212, 255, 0.2);
          border: 2px solid rgba(0, 212, 255, 0.5);
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        "></div>
        <div style="
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #00D4FF;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          position: relative;
          z-index: 1;
        "></div>
      </div>
    `;

    const marker = new atlas.HtmlMarker({
      position: [userLocation.longitude, userLocation.latitude],
      htmlContent: markerHtml,
    });

    mapInstanceRef.current.markers.add(marker);
    userMarkerRef.current = marker;
  }, [userLocation, isLoaded]);

  // Center on user location
  const centerOnUser = useCallback(() => {
    if (mapInstanceRef.current && userLocation) {
      mapInstanceRef.current.setCamera({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: 17,
      });
    }
  }, [userLocation]);

  return (
    <div className={className || 'relative'}>
      <div
        ref={mapRef}
        className="w-full h-full overflow-hidden"
      />
      
      {/* Center on user button */}
      {userLocation && (
        <button
          onClick={centerOnUser}
          className="absolute bottom-4 right-4 w-12 h-12 bg-secondary rounded-full
                     flex items-center justify-center shadow-lg
                     hover:bg-secondary/80 transition-colors"
          aria-label="Center on my location"
        >
          <svg
            className="w-6 h-6 text-accent-blue"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="3" strokeWidth="2" />
            <path
              strokeWidth="2"
              d="M12 2v4m0 12v4m10-10h-4M6 12H2"
            />
          </svg>
        </button>
      )}

      {/* Loading overlay */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
};

export default MapContainer;
